/**
 * Ответ модели в HTML.
 *
 * Санитайзинг здесь не гигиена, а защита. Модель цитирует текст, добытый
 * инструментом из чужой задачи или страницы базы знаний: `<img onerror=…>` в
 * описании задачи, которое кто угодно может отредактировать, доезжает до панели
 * ответом ассистента. Путь «чужой текст → инструмент → модель → innerHTML»
 * открыт по построению, и закрывается он ровно тут.
 *
 * Отсюда белый список, а не чёрный: перечислено то, что модель имеет право
 * нарисовать, всё остальное — включая любой сырой HTML в её ответе — выпадает.
 *
 * Tiptap для этого не годится, хотя он в проекте есть: `setContent` на
 * незакрытой разметке разбирается непредсказуемо, а поток приносит именно её —
 * каждая порция обрывается на середине списка или блока кода.
 */
import DOMPurify from 'dompurify'
import { marked, type TokenizerAndRendererExtension } from 'marked'

/**
 * Что модели позволено нарисовать.
 *
 * Ни `img`, ни `iframe`, ни `style`: картинок ассистент не рисует, а `img` —
 * это `onerror` и запрос на чужой адрес с каждым ответом. Таблицы оставлены:
 * ими модель отвечает на «что просрочено».
 */
const ALLOWED_TAGS = [
  'p',
  'br',
  'hr',
  'strong',
  'em',
  'del',
  'code',
  'pre',
  'blockquote',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'a',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
]

/** Ссылке нужен только адрес: ни классов, ни обработчиков, ни `style`. */
const ALLOWED_ATTR = ['href']

/**
 * Свой ли это адрес.
 *
 * Одна проверка на два места — хук санитайзера и перехват клика в панели:
 * разойдись они, ссылка либо открывалась бы в новой вкладке с потерей
 * разговора, либо перехватывалась бы у чужого адреса. Протокольно
 * относительный `//чужой.сайт` начинается с косой черты, но своим не является.
 */
export const isInternalHref = (href: string): boolean =>
  href.startsWith('/') && !href.startsWith('//')

/**
 * Ключ задачи в тексте ответа: `DEV-1478` становится ссылкой (DEV-1557).
 *
 * Расширение marked, а не замена по готовому HTML: по HTML пришлось бы гадать,
 * что за `DEV-1478` попался — текст, содержимое `<code>` или кусок адреса
 * внутри `href`, и последний превратился бы в ссылку внутри ссылки. На уровне
 * токенов этих случаев просто не возникает: блоки кода до inline-разбора не
 * доходят, а `inLink` отсекает ключ внутри уже размеченной ссылки.
 */
const ISSUE_KEY = /[A-Z][A-Z0-9]{1,9}-\d+/

const issueKeys: TokenizerAndRendererExtension = {
  name: 'issueKey',
  level: 'inline',
  start: (src: string) => src.match(ISSUE_KEY)?.index,
  tokenizer(src: string) {
    if (this.lexer.state.inLink) return undefined

    const match = new RegExp(`^${ISSUE_KEY.source}`).exec(src)

    if (!match) return undefined

    return { type: 'issueKey', raw: match[0], text: match[0] }
  },
  renderer: (token) => `<a href="/issue/${token.text}">${token.text}</a>`,
}

marked.use({ extensions: [issueKeys] })

/**
 * Чужие ссылки открываются в новой вкладке, свои — в этом же приложении.
 *
 * `rel` обязателен: без `noopener` открытая страница получает `window.opener` и
 * может увести исходную вкладку на свой адрес. Ставится хуком, а не разрешённым
 * атрибутом, — тогда `target` и `rel` берутся отсюда, а не из ответа модели.
 *
 * Своей ссылке `target` не нужен: панель и тред живут в памяти вкладки, и новая
 * вкладка открыла бы задачу ценой всего разговора о ней.
 */
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName !== 'A') return
  if (isInternalHref(node.getAttribute('href') ?? '')) return

  node.setAttribute('target', '_blank')
  node.setAttribute('rel', 'noopener noreferrer')
})

export const renderMarkdown = (text: string): string => {
  // Незакрытый блок кода — норма посреди потока, а не ошибка: marked закрывает
  // его сам и отдаёт валидный HTML.
  const html = marked.parse(text, { async: false, breaks: true, gfm: true })

  // Текст из выброшенных тегов остаётся (умолчание KEEP_CONTENT): модель
  // написала `<div>важное</div>` — «важное» человеку нужнее пустоты. Содержимое
  // опасных тегов при этом выпадает вместе с ними: script и style у DOMPurify
  // в FORBID_CONTENTS, и текст из них не сохраняется ни при каких настройках.
  return DOMPurify.sanitize(html, { ALLOWED_TAGS, ALLOWED_ATTR })
}
